/**
 * planLabels — display-name mapping for customer plan ids.
 *
 * DB canonical stays `"free" | "plus" | "premium"` (the id) with a
 * canonical `.name` field of `"Free" | "Plus" | "Premium"`. The
 * customer-facing UI has moved off "Free" → "Basic" for clarity
 * ("Free" tests as "no service" in the customer wording study;
 * "Basic" tests as "the entry tier that always exists"). Server
 * writes are UNCHANGED — this is a display-layer rename only, so
 * we don't have to migrate any DB rows, admin edit workflows, or
 * seed scripts.
 *
 * Every render site that showed a plan label used to read `.name`
 * off the API row directly, e.g. `{plan.name}` → "Free". After
 * this dispatch, those sites route through `getPlanDisplayName(id)`
 * so:
 *   - `"free"    → "Basic"`   (rename)
 *   - `"plus"    → "Plus"`    (unchanged)
 *   - `"premium" → "Premium"` (unchanged)
 *   - unknown / custom id → the id verbatim (fallback contract).
 *
 * When a future dispatch renames another plan (or adds a new one),
 * update the map here — do NOT sprinkle string checks across the
 * render sites. The Jest suite (`planLabels.test.js`) locks the
 * three canonical mappings + the fallback contract.
 */

export const CUSTOMER_PLAN_DISPLAY_NAMES = Object.freeze({
  free: "Basic",
  plus: "Plus",
  premium: "Premium",
});

/**
 * Return the display name for a customer_plan id.
 *
 * @param {string|null|undefined} planId — the canonical plan id
 *   (`"free" | "plus" | "premium"` today). Anything else falls
 *   through to the id verbatim so an admin-created custom plan
 *   still renders its real name.
 * @returns {string}
 */
export function getPlanDisplayName(planId) {
  if (planId == null) return "";
  return CUSTOMER_PLAN_DISPLAY_NAMES[planId] ?? String(planId);
}
